'use client';

import { useCallback, useRef, useState } from 'react';

import { creatorInfoFromStoreProfile } from '@ps/functions/bio/creatorInfo';
import { useLogin } from '@ps/ui/contexts/auth';
import { cn } from '@ps/ui/utils';
import { Send, UserRound, X } from 'lucide-react';

import {
  type BasicUserProfileInfo,
  type PopClipVideoInfo,
  useClipComments,
} from '../../functions';
import { CommentItem } from './CommentItem';

type CommentsProps = {
  video: PopClipVideoInfo;
  currentUserId?: string;
  currentUserMeta?: BasicUserProfileInfo | null;
  isRealEstateListing?: boolean;
  onClose?: () => void;
  className?: string;
};

export function Comments({
  video,
  currentUserId,
  currentUserMeta,
  isRealEstateListing,
  onClose,
  className,
}: CommentsProps) {
  const {
    comments,
    addComment,
    retryComment,
    deleteComment,
    fetchMoreComments,
    isCommentLoading,
    isFetchingInitialComments,
    isFetchingMoreComments,
    hasMoreComments,
  } = useClipComments(video.id);

  const [commentText, setCommentText] = useState('');
  const listRef = useRef<HTMLDivElement>(null);
  const login = useLogin();

  const onSendComment = useCallback(async () => {
    if (!commentText.trim()) return;
    // Guest — open the login modal instead of silently dropping the comment.
    // The typed text stays in the box (soft login return), so after signing in
    // the visitor can hit Send again. Mirrors RN's comment-with-login gate.
    // Brand the modal with the clip owner (from `video.author`) so it shows the
    // seller avatar + name, matching the bio surfaces.
    if (!currentUserId) {
      login({
        creatorInfo: video.author
          ? creatorInfoFromStoreProfile(video.author)
          : undefined,
      });
      return;
    }
    const text = commentText.trim();
    setCommentText('');
    await addComment(text, currentUserId, currentUserMeta ?? null);
    listRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [commentText, currentUserId, currentUserMeta, addComment, login]);

  const handleRetryComment = useCallback(
    (id: string) => {
      if (!currentUserId) return;
      void retryComment(id, currentUserId, currentUserMeta ?? null);
    },
    [currentUserId, currentUserMeta, retryComment],
  );

  const handleDeleteComment = useCallback(
    (clipId: string, timestamp: number) => {
      if (!currentUserId) return;
      void deleteComment(clipId, timestamp, currentUserId);
    },
    [currentUserId, deleteComment],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const el = e.currentTarget;
      if (
        el.scrollHeight - el.scrollTop - el.clientHeight < 100 &&
        hasMoreComments &&
        !isFetchingMoreComments
      ) {
        void fetchMoreComments();
      }
    },
    [hasMoreComments, isFetchingMoreComments, fetchMoreComments],
  );

  return (
    <div className={cn('bg-surface-1 flex h-full flex-col', className)}>
      {/* Header */}
      <div className="border-raise/10 relative flex items-center justify-center border-b py-3">
        <h3 className="text-content-1 text-base font-bold">Comments</h3>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="text-content-1 hover:text-raise/70 absolute right-4 top-1/2 -translate-y-1/2"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Comments list */}
      <div
        ref={listRef}
        className="no-scrollbar flex-1 overflow-y-auto px-5 pt-5"
        onScroll={handleScroll}
      >
        {isFetchingInitialComments ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <div className="border-raise/30 h-8 w-8 animate-spin rounded-full border-2 border-t-white" />
          </div>
        ) : comments.length === 0 ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <p className="text-raise/40 text-center text-sm">
              No comments yet. Be the first to comment!
            </p>
          </div>
        ) : (
          <>
            {comments.map(comment => (
              <CommentItem
                key={comment.id}
                item={comment}
                clipId={video.id}
                currentUserId={currentUserId}
                currentUserMeta={currentUserMeta}
                onRetryComment={handleRetryComment}
                onDeleteComment={handleDeleteComment}
              />
            ))}
            {isFetchingMoreComments && (
              <div className="flex justify-center py-3">
                <div className="border-raise/30 h-5 w-5 animate-spin rounded-full border-2 border-t-white" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Comment input — hidden for non-logged-in users on real estate listings */}
      {!(isRealEstateListing && !currentUserId) && (
        <div className="border-raise/10 bg-line border-t px-5 py-3">
          <div className="flex items-center gap-2.5">
            {currentUserMeta?.avatar ? (
              <img
                src={currentUserMeta.avatar}
                alt=""
                className="h-10 w-10 shrink-0 rounded-full object-cover"
              />
            ) : (
              <div className="bg-raise/20 flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
                <UserRound className="text-content-1 h-6 w-6" />
              </div>
            )}
            <div className="flex flex-1 items-center rounded-full border border-white px-3 py-2">
              <input
                type="text"
                value={commentText}
                onChange={e => setCommentText(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void onSendComment();
                }}
                placeholder="Add a comment..."
                className="text-content-1 placeholder:text-content-placeholder flex-1 bg-transparent text-sm outline-none"
              />
              <button
                type="button"
                onClick={onSendComment}
                disabled={isCommentLoading || !commentText.trim()}
                className="text-content-1 ml-2 disabled:opacity-40"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
